import { useEffect, useLayoutEffect, useState } from "react";
import Lenis from "lenis";
import { AnimatePresence, motion, MotionConfig } from "framer-motion";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { Hero } from "@/components/lunar/Hero";
import { Follow } from "@/components/lunar/Follow";
import { HowItWorks } from "@/components/lunar/HowItWorks";
import { PrivateWaves } from "@/components/lunar/PrivateWaves";
import { SocialVisualStrip } from "@/components/lunar/SocialVisualStrip";
import { WhoMightYouFind } from "@/components/lunar/WhoMightYouFind";
import { WhereToUse } from "@/components/lunar/WhereToUse";
import { Groups } from "@/components/lunar/Groups";
import { Manifesto } from "@/components/lunar/Manifesto";
import { FinalCta } from "@/components/lunar/FinalCta";
import { Footer } from "@/components/lunar/Footer";
import { RouteSeo } from "@/components/lunar/RouteSeo";
import Privacy from "@/pages/Privacy";
import Terms from "@/pages/Terms";
import VenueKit from "@/pages/VenueKit";
import Events from "@/pages/Events";
import Networking from "@/pages/Networking";
import Download from "@/pages/Download";
import Safety from "@/pages/Safety";
import Support from "@/pages/Support";
import ChildSafety from "@/pages/ChildSafety";
import "@/App.css";

const Landing = () => {
    const [socialOpen, setSocialOpen] = useState(false);

    const toggleSocial = () => {
        setSocialOpen((open) => {
            if (!open) {
                setTimeout(
                    () =>
                        document
                            .getElementById("hero-phone-wrap")
                            ?.scrollIntoView({ behavior: "smooth", block: "start" }),
                    250
                );
            }
            return !open;
        });
    };

    return (
        <>
            <main>
                <Hero socialOpen={socialOpen} onToggleSocial={toggleSocial} />
                <Follow />
                <AnimatePresence initial={false}>
                    {socialOpen && (
                        <motion.div
                            key="social-content"
                            id="social-content"
                            data-testid="social-content"
                            initial={{ height: 0, opacity: 0 }}
                            animate={{ height: "auto", opacity: 1 }}
                            exit={{ height: 0, opacity: 0 }}
                            transition={{ duration: 0.65, ease: [0.22, 1, 0.36, 1] }}
                            style={{ overflow: "hidden" }}
                        >
                            <HowItWorks />
                            <PrivateWaves />
                            <SocialVisualStrip />
                            <WhoMightYouFind />
                            <WhereToUse />
                            <Groups />
                            <Manifesto />
                            <FinalCta />
                        </motion.div>
                    )}
                </AnimatePresence>
            </main>
            <Footer />
        </>
    );
};

if (typeof window !== "undefined" && "scrollRestoration" in window.history) {
    window.history.scrollRestoration = "manual";
}

const ScrollToTop = () => {
    const { pathname } = useLocation();
    useLayoutEffect(() => {
        window.__lunarLenis?.scrollTo(0, { immediate: true });
        window.scrollTo(0, 0);
        document.documentElement.scrollTop = 0;
        document.body.scrollTop = 0;
    }, [pathname]);
    useEffect(() => {
        const reset = (event) => {
            window.__lunarLenis?.scrollTo(0, { immediate: true });
            window.scrollTo(0, 0);
            if (event?.persisted) {
                [80, 240, 500, 900, 1400].forEach((delay) => {
                    window.setTimeout(() => window.__lunarLenis?.scrollTo(0, { immediate: true }), delay);
                    window.setTimeout(() => window.scrollTo(0, 0), delay);
                });
            }
        };
        window.addEventListener("pageshow", reset);
        return () => window.removeEventListener("pageshow", reset);
    }, []);
    return null;
};

const NotFound = () => (
    <>
        <main
            data-testid="not-found-page"
            className="mx-auto flex min-h-[62vh] max-w-3xl flex-col justify-center px-6 py-24"
        >
            <p className="text-xs font-bold uppercase tracking-[0.26em] text-[#899B6A]">
                Page not found
            </p>
            <h1 className="mt-4 font-display text-4xl font-semibold tracking-[-0.03em] text-[#1D201A] sm:text-5xl">
                This page has moved.
            </h1>
            <p className="mt-5 text-base leading-7 text-[#5C5F58]">
                The page you are looking for is not available. Return to Lunar to continue.
            </p>
            <a
                href="/"
                data-testid="not-found-home-link"
                className="mt-8 inline-flex w-fit rounded-full bg-[#2C3618] px-5 py-3 text-sm font-medium text-[#F9F8F4] transition-colors duration-200 hover:bg-[#4A5D23]"
            >
                Return to Lunar
            </a>
        </main>
        <Footer />
    </>
);

function App() {
    useEffect(() => {
        const reducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
        const finePointer = window.matchMedia("(pointer: fine)").matches;
        if (reducedMotion || !finePointer) return undefined;

        const lenis = new Lenis({ lerp: 0.1, smoothWheel: true });
        lenis.scrollTo(0, { immediate: true });
        window.__lunarLenis = lenis;
        let rafId;
        const loop = (time) => {
            lenis.raf(time);
            rafId = requestAnimationFrame(loop);
        };
        rafId = requestAnimationFrame(loop);
        return () => {
            cancelAnimationFrame(rafId);
            lenis.destroy();
            delete window.__lunarLenis;
        };
    }, []);

    return (
        <MotionConfig reducedMotion="user">
            <BrowserRouter>
                <ScrollToTop />
                <div className="min-h-screen bg-[#F9F8F4] text-[#1D201A]">
                    <div className="grain-overlay" aria-hidden="true" />
                    <Routes>
                        <Route path="/" element={<Landing />} />
                        <Route path="/privacy" element={<Privacy />} />
                        <Route path="/terms" element={<Terms />} />
                        <Route path="/venue-kit" element={<VenueKit />} />
                        <Route path="/events" element={<Events />} />
                        <Route path="/networking" element={<Networking />} />
                        <Route path="/download" element={<Download />} />
                        <Route path="/safety" element={<Safety />} />
                        <Route path="/support" element={<Support />} />
                        <Route path="/child-safety" element={<ChildSafety />} />
                        <Route path="*" element={<NotFound />} />
                    </Routes>
                    <RouteSeo />
                    <Toaster position="top-center" />
                </div>
            </BrowserRouter>
        </MotionConfig>
    );
}

export default App;
